import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import PropTypes from "prop-types";

import fileDownload from "js-file-download";
import moment from "moment";
import BulkPayoutModal from "../../Components/BulkPayoutModal/BulkPayoutModal";
import BulkRevenueShareModal from "../../Components/BulkRevenueShareModal";
import ManageSuperPartnerModal from "../../Components/ManageSuperPartnerModal";
import RefernEarnPartnerDetailsExpand from "../../Components/RefernEarnPartnerDetailsExpand";
import UpdateRevenueShareModal from "../../Components/UpdateRevenueShareModal";
import useDebounce from "../../hooks/useDebounce";
import {
  createRefernEarnPayoutToPartner,
  getRefernEarnNonEarningPartners,
  suspendPartner,
  unSuspendPartner,
} from "../api";

const NonEarningPartnersList = ({ setCurrentScreen }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    limit: "", // onetime | subscription | onetime-subscription
    page: 1,
    pageSize: 10,
    search: "",
    startDate: "",
    endDate: "",
    superPartner: "all", // all | enabled | disabled
  });
  const [loading, setLoading] = useState(false);
  const [bulkRevenueShareModalVisible, setBulkRevenueShareModalVisible] =
    useState(false);
  const [bulkPayoutModalVisible, setBulkPayoutModalVisible] = useState(false);
  const [revenueShareModalVisible, setRevenueShareModalVisible] =
    useState(false);
  const [payoutModalVisible, setPayoutModalVisible] = useState(false);
  const [currentPartnerForRevenueShare, setCurrentPartnerForRevenueShare] =
    useState(null);
  const [superPartnerModalVisible, setSuperPartnerModalVisible] =
    useState(false);
  const [currentPartnerForSuperPartner, setCurrentPartnerForSuperPartner] =
    useState(null);
  const [currentUserForPayout, setCurrentUserForPayout] = useState(null);
  const [payoutForm] = Form.useForm();

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetRefernEarnNonEarningPartners = async (csv = false) => {
    setLoading(true);

    let hide;
    if (csv) {
      hide = message.loading("Generating CSV...", 0);
    }

    try {
      const res = await getRefernEarnNonEarningPartners(filters, csv);

      if (csv) {
        fileDownload(res, "refern_earn_non_earning_partners.csv");
      } else if (res?.code === 200) {
        setData(res.result.partners.map((item, index) => ({ ...item, index })));
        setTotal(res.result.totalPartners);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const handleCreateRefernEarnPayoutForPartner = async (values, id) => {
    setLoading(true);
    try {
      const res = await createRefernEarnPayoutToPartner(values, id);
      if (res?.code === 201 || res?.code === 200) {
        payoutForm.resetFields();
        setPayoutModalVisible(false);
        handleGetRefernEarnNonEarningPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendRefernEarnPartner = async (id) => {
    setLoading(true);
    try {
      const res = await suspendPartner(id);
      if (res?.code === 201) {
        message.success("Partner suspended successfully");
        handleGetRefernEarnNonEarningPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnSuspendRefernEarnPartner = async (id) => {
    setLoading(true);
    try {
      const res = await unSuspendPartner(id);
      if (res?.code === 201) {
        message.success("Partner un-suspended successfully");
        handleGetRefernEarnNonEarningPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Typography style={{}}>{record?.name}</Typography>
            {record?.isReferEarnSuperPartner ? (
              <Tooltip title="Super Partner">
                <Tag color="gold">Super</Tag>
              </Tooltip>
            ) : null}
          </Space>
          <Typography>{record?.email}</Typography>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12 }}
            copyable={{ text: record?._id }}
          >
            Referral ID: {record?._id}
          </Typography.Text>
          <Typography>{record?.phone}</Typography>
          <Typography>({record?.host})</Typography>
        </Space>
      ),
    },
    // created at
    {
      title: "Onboarded on",
      dataIndex: "refernEarnStatusUpdatedAt",
      key: "refernEarnStatusUpdatedAt",
      render: (_, record) => (
        <Typography>
          {moment(record?.refernEarnStatusUpdatedAt).format("DD MMM YYYY")}
        </Typography>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Dropdown.Button
            type="primary"
            overlay={
              <Menu>
                <Menu.Item
                  onClick={() => {
                    setCurrentUserForPayout(record);
                    setPayoutModalVisible(true);
                  }}
                >
                  Manage Payout
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setCurrentPartnerForRevenueShare(record);
                    setRevenueShareModalVisible(true);
                  }}
                >
                  Update Revenue Share
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    record.isReferEarnSuspended
                      ? handleUnSuspendRefernEarnPartner(record._id)
                      : handleSuspendRefernEarnPartner(record._id)
                  }
                >
                  {record.isReferEarnSuspended
                    ? "Unsuspend Partner"
                    : "Suspend Partner"}
                </Menu.Item>

                <Menu.Item
                  onClick={() => {
                    setCurrentPartnerForSuperPartner(record);
                    setSuperPartnerModalVisible(true);
                  }}
                >
                  {record.isReferEarnSuperPartner
                    ? "Remove Super Partner"
                    : "Mark as Super Partner"}
                </Menu.Item>
              </Menu>
            }
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    handleGetRefernEarnNonEarningPartners();
  }, [filters]);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <Row gutter={6}>
        <Col span={24}>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
      </Row>

      <Row gutter={6} style={{ marginTop: "10px" }}>
        <Col span={24}>
          <Space>
            <DatePicker.RangePicker
              value={[filters.startDate, filters.endDate]}
              onChange={(dates) => {
                setFilters({
                  ...filters,
                  startDate: dates?.[0].startOf("day"),
                  endDate: dates?.[1].endOf("day"),
                });
              }}
              allowClear
            />
            <Typography.Text>Super Partner:</Typography.Text>
            <Select
              style={{ width: 140 }}
              value={filters.superPartner}
              onChange={(value) => {
                setFilters({
                  ...filters,
                  superPartner: value,
                  page: 1,
                });
              }}
            >
              <Select.Option value="enabled">Enabled</Select.Option>
              <Select.Option value="disabled">Disabled</Select.Option>
              <Select.Option value="all">All</Select.Option>
            </Select>
            <Button
              type="primary"
              onClick={() => {
                handleGetRefernEarnNonEarningPartners(true);
              }}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setBulkPayoutModalVisible(true);
              }}
            >
              Manage Payouts
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setBulkRevenueShareModalVisible(true);
              }}
            >
              Manage Revenue Share
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total,
          pageSize: filters.pageSize,
          onChange: (p) => setFilters({ ...filters, page: p }),
        }}
        expandable={{
          expandedRowRender: (record) => (
            <RefernEarnPartnerDetailsExpand
              userDetails={record}
              setCurrentScreen={setCurrentScreen}
            />
          ),
        }}
        rowKey={(record) => record._id}
      />

      <BulkPayoutModal
        visible={bulkPayoutModalVisible}
        onClose={() => setBulkPayoutModalVisible(false)}
        onSuccess={() => {
          handleGetRefernEarnNonEarningPartners();
        }}
      />

      <BulkRevenueShareModal
        visible={bulkRevenueShareModalVisible}
        onClose={() => setBulkRevenueShareModalVisible(false)}
        onSuccess={() => {
          handleGetRefernEarnNonEarningPartners();
        }}
      />

      <UpdateRevenueShareModal
        visible={revenueShareModalVisible}
        onClose={() => {
          setRevenueShareModalVisible(false);
          setCurrentPartnerForRevenueShare(null);
        }}
        partner={currentPartnerForRevenueShare}
        onSuccess={() => {
          handleGetRefernEarnNonEarningPartners();
        }}
      />

      <ManageSuperPartnerModal
        visible={superPartnerModalVisible}
        onClose={() => {
          setSuperPartnerModalVisible(false);
          setCurrentPartnerForSuperPartner(null);
        }}
        partner={currentPartnerForSuperPartner}
        onSuccess={() => {
          handleGetRefernEarnNonEarningPartners();
        }}
      />

      <Modal
        title="Manage Payout"
        visible={payoutModalVisible}
        onCancel={() => {
          setPayoutModalVisible(false);
        }}
        destroyOnClose
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => {
              payoutForm.validateFields().then((values) => {
                const validatedValues = values;

                if (validatedValues.period) {
                  validatedValues.period = {
                    startDate: validatedValues.period[0]
                      .startOf("day")
                      .toISOString(),
                    endDate: validatedValues.period[1]
                      .endOf("day")
                      .toISOString(),
                  };
                }

                if (validatedValues.releaseDate) {
                  validatedValues.releaseDate = validatedValues.releaseDate
                    .startOf("day")
                    .toISOString();
                }

                if (validatedValues.amount) {
                  validatedValues.amount = Number(validatedValues.amount);
                }

                handleCreateRefernEarnPayoutForPartner(
                  validatedValues,
                  currentUserForPayout?._id
                );
              });
            }}
            style={{ width: "100%" }}
          >
            Create
          </Button>,
        ]}
      >
        {currentUserForPayout ? (
          <Card style={{ marginBottom: "16px" }}>
            <Space size="small" direction="vertical">
              <Typography style={{}}>{currentUserForPayout?.name}</Typography>
              <Typography>{currentUserForPayout?.email}</Typography>
              <Typography>{currentUserForPayout?.phone}</Typography>
              <Typography>({currentUserForPayout?.host})</Typography>
            </Space>
          </Card>
        ) : null}

        <Form form={payoutForm} layout="vertical">
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="amount"
              label="Payout amount"
              rules={[
                {
                  required: true,
                  message: "Please enter payout amount",
                },
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Payout amount should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="transactionType"
              label="Transaction Type"
              rules={[
                { required: true, message: "Please select transaction type" },
              ]}
            >
              <Select>
                <Select.Option value="credit">Credit</Select.Option>
                <Select.Option value="debit">Debit</Select.Option>
              </Select>
            </Form.Item>
          </Row>

          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="period"
              label="Payout Period"
              rules={[
                {
                  required: true,
                  message: "Please select payout period",
                },
              ]}
            >
              <DatePicker.RangePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.transactionType !== curr.transactionType
              }
            >
              {({ getFieldValue }) => {
                const isCredit = getFieldValue("transactionType") === "credit";
                return (
                  <Form.Item
                    style={{ flex: 1 }}
                    name="releaseDate"
                    label="Release Date"
                    rules={[
                      {
                        required: !isCredit,
                        message: "Please select release date",
                      },
                    ]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>

          <Form.Item name="remark" label="Remark">
            <Input.TextArea placeholder="e.g. Payout for the month of May" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

NonEarningPartnersList.propTypes = {
  setCurrentScreen: PropTypes.func.isRequired,
  screenData: PropTypes.shape({}),
};

export default NonEarningPartnersList;
